import type { ReportRow } from '../../models/report.types'
import { resolveReportImageUrl } from '../reportMediaUrl'

import { category, finiteNumber } from './chartData'

import { resolveStaticPlatformLogo } from '@/shared/ui/logo'

export function combinedCategory(row: ReportRow, fields: readonly string[]): string {
  return fields.map((field) => category(row[field])).join(' / ')
}

export interface CategorySlot {
  key: string
  label: string
  occurrence: number
  mediaSrc: string | null
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

/** Shared visual lookup for category axes and point-based legends. Row images win; otherwise chart
 * category logos come only from the row's `marketing_channel` value. */
export function categoryVisualSrc(row: ReportRow, mediaFields: readonly string[]): string | null {
  const mediaSrc =
    mediaFields.map((field) => resolveReportImageUrl(row[field])).find(Boolean) ?? null
  if (mediaSrc) return mediaSrc
  return resolveStaticPlatformLogo(row.marketing_channel) ?? null
}

export function categoryAxisLabels(
  slots: readonly CategorySlot[],
  // `compare_column` only: the Actual series' formatted value per slot, as a bold line above the
  // category name — reads as part of the axis tick rather than a floating per-bar data label.
  values?: readonly string[],
): {
  useHTML: boolean
  autoRotation: number[]
  style: { color: string; fontSize: string; fontWeight: string }
  formatter: (this: { pos: number }) => string
} {
  return {
    useHTML: true,
    style: { color: 'var(--ui-chart-label)', fontSize: '10px', fontWeight: '400' },
    // A `useHTML` label's real size isn't known when Highcharts picks an `autoRotation` angle,
    // so its rotate() transform carries NaN coordinates (invalid, console-noisy SVG). The label
    // already wraps via `whitespace-normal` below, so rotation was never needed — disable it.
    autoRotation: [],
    formatter(this: { pos: number }): string {
      const slot = slots[this.pos]
      if (!slot) return ''
      const label = escapeHtml(slot.label)
      const value = values?.[this.pos]
      const valueLine = value
        ? `<span class="ui-chart-axis-value block whitespace-normal text-center text-xs font-bold leading-4 text-[color:var(--ui-chart-axis-title)]">${escapeHtml(value)}</span>`
        : ''
      if (!slot.mediaSrc) {
        return `${valueLine}<span class="ui-chart-axis-label block w-32 whitespace-normal text-center text-2xs leading-4 text-[color:var(--ui-chart-label)]">${label}</span>`
      }
      return `${valueLine}<span class="ui-chart-axis-media-label grid w-32 justify-items-center gap-2xs whitespace-normal text-2xs text-[color:var(--ui-chart-label)]"><img class="h-[17px] w-[17px] rounded-sm object-contain" src="${escapeHtml(slot.mediaSrc)}" alt=""><span class="line-clamp-3 text-center leading-4">${label}</span></span>`
    },
  }
}

/** Preserve duplicate backend rows without summing non-additive metrics such as rates. For
 * grouped series, repeated labels are aligned by occurrence and missing points remain zero. */
export function categorySlots(
  rows: readonly ReportRow[],
  axisFields: readonly string[],
  groupingFields: readonly string[],
  mediaFields: readonly string[],
): CategorySlot[] {
  const order: { key: string; label: string; mediaSrc: string | null }[] = []
  const seen = new Set<string>()
  const countsByGroup = new Map<string, Map<string, number>>()
  const maximumByLabel = new Map<string, number>()

  for (const row of rows) {
    const key = combinedCategory(row, axisFields)
    const labelFields = axisFields.filter((field) => !mediaFields.includes(field))
    const label = combinedCategory(row, labelFields)
    const mediaSrc = categoryVisualSrc(row, mediaFields)
    const group = combinedCategory(row, groupingFields)
    if (!seen.has(key)) {
      seen.add(key)
      order.push({ key, label: label || 'Image', mediaSrc })
    }
    const counts = countsByGroup.get(group) ?? new Map<string, number>()
    countsByGroup.set(group, counts)
    const count = (counts.get(key) ?? 0) + 1
    counts.set(key, count)
    maximumByLabel.set(key, Math.max(maximumByLabel.get(key) ?? 0, count))
  }

  return order.flatMap(({ key, label, mediaSrc }) =>
    Array.from({ length: maximumByLabel.get(key) ?? 0 }, (_, occurrence) => ({
      key,
      label,
      occurrence,
      mediaSrc,
    })),
  )
}

/** One value per category slot, keeping repeated category tuples apart rather than summing them. */
export function valuesByCategoryOccurrence(
  rows: readonly ReportRow[],
  axisFields: readonly string[],
  metric: string,
  slots: readonly CategorySlot[],
): number[] {
  const values = new Map<string, number[]>()
  for (const row of rows) {
    const key = combinedCategory(row, axisFields)
    const bucket = values.get(key) ?? []
    bucket.push(finiteNumber(row[metric]))
    values.set(key, bucket)
  }
  return slots.map(({ key, occurrence }) => values.get(key)?.[occurrence] ?? 0)
}
